import React, { useEffect, useMemo, useState } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-python";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-json";
import { Spinner } from "@kui-react/spinner";
import { renderMarkdown } from "./markdownRenderer";
import { FlatCard } from "components/UI-lib";
import badnotebook from "components/Notebooks/badnotebook.png";

const CORS_PROXY =
  "https://us-central1-brev-testing-1.cloudfunctions.net/cors-proxy";

interface NotebookOutput {
  output_type: string;
  text?: string | string[];
  name?: string;
  data?: Record<string, string | string[]>;
  ename?: string;
  evalue?: string;
  traceback?: string[];
  execution_count?: number | null;
}

interface NotebookCell {
  cell_type: string;
  source?: string | string[];
  outputs?: NotebookOutput[];
  execution_count?: number | null;
}

interface JupyterNotebook {
  cells?: NotebookCell[];
  metadata?: {
    language_info?: { name?: string };
    kernelspec?: { language?: string };
  };
}

interface NotebookViewerProps {
  file: string;
}

const ANSI_REGEX = /\x1b\[[0-9;]*m/g;

const joinSource = (source?: string | string[]): string => {
  if (Array.isArray(source)) return source.join("");
  return source || "";
};

const stripAnsi = (text: string): string => text.replace(ANSI_REGEX, "");

export const toRawNotebookUrl = (url: string): string => {
  try {
    const parsed = new URL(url);

    if (parsed.hostname === "github.com") {
      const match = parsed.pathname.match(
        /^\/([^/]+)\/([^/]+)\/(?:blob|raw)\/(.+)$/
      );
      if (match) {
        return `https://raw.githubusercontent.com/${match[1]}/${match[2]}/${match[3]}`;
      }
    }

    if (parsed.hostname.endsWith("gitlab.com") && url.includes("/-/blob/")) {
      return url.replace("/-/blob/", "/-/raw/");
    }

    return url;
  } catch {
    return url;
  }
};

const getLanguage = (notebook: JupyterNotebook): string => {
  const lang =
    notebook.metadata?.language_info?.name ||
    notebook.metadata?.kernelspec?.language ||
    "python";
  return lang.toLowerCase();
};

const highlightCode = (code: string, language: string): string => {
  const grammar = Prism.languages[language] || Prism.languages.python;
  try {
    return Prism.highlight(code, grammar, language);
  } catch {
    return code;
  }
};

const OutputBlock: React.FC<{ output: NotebookOutput }> = ({ output }) => {
  if (output.output_type === "stream") {
    return (
      <pre className="text-xs text-gray-200 whitespace-pre-wrap overflow-x-auto px-4 py-2">
        {stripAnsi(joinSource(output.text))}
      </pre>
    );
  }

  if (output.output_type === "error") {
    return (
      <pre className="text-xs text-red-400 whitespace-pre-wrap overflow-x-auto px-4 py-2">
        {stripAnsi((output.traceback || []).join("\n")) ||
          `${output.ename}: ${output.evalue}`}
      </pre>
    );
  }

  if (
    output.output_type === "execute_result" ||
    output.output_type === "display_data"
  ) {
    const data = output.data || {};

    if (data["image/png"]) {
      return (
        <div className="px-4 py-2">
          <img
            src={`data:image/png;base64,${joinSource(data["image/png"])}`}
            alt="notebook output"
            className="max-w-full h-auto rounded"
          />
        </div>
      );
    }

    if (data["image/jpeg"]) {
      return (
        <div className="px-4 py-2">
          <img
            src={`data:image/jpeg;base64,${joinSource(data["image/jpeg"])}`}
            alt="notebook output"
            className="max-w-full h-auto rounded"
          />
        </div>
      );
    }

    if (data["image/svg+xml"]) {
      return (
        <div
          className="px-4 py-2 max-w-full overflow-x-auto"
          dangerouslySetInnerHTML={{ __html: joinSource(data["image/svg+xml"]) }}
        />
      );
    }

    if (data["text/html"]) {
      return (
        <div
          className="px-4 py-2 max-w-full overflow-x-auto text-gray-200 text-sm"
          dangerouslySetInnerHTML={{ __html: joinSource(data["text/html"]) }}
        />
      );
    }

    if (data["text/plain"]) {
      return (
        <pre className="text-xs text-gray-200 whitespace-pre-wrap overflow-x-auto px-4 py-2">
          {stripAnsi(joinSource(data["text/plain"]))}
        </pre>
      );
    }
  }

  return null;
};

const CodeCell: React.FC<{ cell: NotebookCell; language: string }> = ({
  cell,
  language,
}) => {
  const code = joinSource(cell.source);
  const highlighted = useMemo(
    () => highlightCode(code, language),
    [code, language]
  );
  const outputs = cell.outputs || [];

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-zinc-800">
      <pre className="bg-black p-4 overflow-x-auto text-sm m-0">
        <code
          className={`language-${language}`}
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      </pre>
      {outputs.length > 0 && (
        <div className="bg-zinc-900 border-t border-zinc-800">
          {outputs.map((output, idx) => (
            <OutputBlock key={idx} output={output} />
          ))}
        </div>
      )}
    </div>
  );
};

const MarkdownCell: React.FC<{ cell: NotebookCell }> = ({ cell }) => (
  <div
    className="max-w-none my-2"
    dangerouslySetInnerHTML={{ __html: renderMarkdown(joinSource(cell.source)) }}
  />
);

const NotebookViewer: React.FC<NotebookViewerProps> = ({ file }) => {
  const [notebook, setNotebook] = useState<JupyterNotebook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchNotebook = async () => {
      setLoading(true);
      setError("");
      setNotebook(null);

      const fetchFromUrl = async (url: string): Promise<string> => {
        const response = await fetch(CORS_PROXY, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        });
        return response.text();
      };

      const parseNotebook = (text: string): JupyterNotebook | null => {
        try {
          const parsed = JSON.parse(text);
          if (parsed && Array.isArray(parsed.cells)) {
            return parsed as JupyterNotebook;
          }
        } catch {
          // not valid JSON / not a notebook
        }
        return null;
      };

      try {
        const rawUrl = toRawNotebookUrl(file);
        let text = await fetchFromUrl(rawUrl);
        let parsed = parseNotebook(text);

        // Fallback to the master branch if main was not found
        if (!parsed && rawUrl.includes("/main/")) {
          text = await fetchFromUrl(rawUrl.replace("/main/", "/master/"));
          parsed = parseNotebook(text);
        }

        if (cancelled) return;

        if (parsed) {
          setNotebook(parsed);
        } else {
          setError("Unable to load this notebook.");
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Unable to load this notebook.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotebook();

    return () => {
      cancelled = true;
    };
  }, [file]);

  const language = useMemo(
    () => (notebook ? getLanguage(notebook) : "python"),
    [notebook]
  );

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-8">
        <Spinner className="w-24 h-24" />
        <p className="text-sm text-secondary mt-2">Loading Notebook...</p>
      </div>
    );
  }

  if (error || !notebook) {
    return (
      <div className="flex flex-col justify-center items-center mt-6">
        <img src={badnotebook} alt="Notebook not found" />
      </div>
    );
  }

  return (
    <FlatCard>
      <div className="max-w-none">
        {(notebook.cells || []).map((cell, idx) => {
          if (cell.cell_type === "markdown") {
            return <MarkdownCell key={idx} cell={cell} />;
          }
          if (cell.cell_type === "code") {
            return <CodeCell key={idx} cell={cell} language={language} />;
          }
          return null;
        })}
      </div>
    </FlatCard>
  );
};

export default NotebookViewer;
